<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { globalState, getUser, getUserAsync, refreshUsage, setToken } from '@/lib/user';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useRouter } from 'vue-router';
import UserMenu from '@/components/auth/UserMenu.vue';
import CreditIcon from '@/components/icons/Credit.vue';
import AuthModal from '@/components/auth/AuthModal.vue';

const user = computed(() => globalState.user);
const router = useRouter();

const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

onMounted(() => {
  getUser();
  getUserAsync();
  refreshUsage().catch((e) => {
    if (e.code === 'TOKEN_EXPIRED') {
      setToken('');
      router.push({ name: 'SignIn' });
    }
  });
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const remaining = computed(() => {
  return Math.max(0, globalState.usage.total - globalState.usage.used);
});

// 数字滚动动画相关
const displayValue = ref(0);
const isAnimating = ref(false);
const prevValue = ref(0);

// 监听 remaining 变化，触发滚动动画
watch(
  remaining,
  (newVal, oldVal) => {
    if (oldVal !== undefined && newVal > oldVal) {
      // 积分增加时触发动画
      prevValue.value = oldVal;
      isAnimating.value = true;
      displayValue.value = newVal;

      // 动画结束后重置状态
      setTimeout(() => {
        isAnimating.value = false;
      }, 600);
    } else {
      displayValue.value = newVal;
    }
  },
  { immediate: true }
);

const isMobileMenuOpen = ref(false);
const showAuthModal = ref(false);
const authLoginType = ref('sign-up');

function logout() {
  setToken('');
  getUser();
}

function openAuthModal(type = 'sign-up') {
  authLoginType.value = type;
  showAuthModal.value = true;
  isMobileMenuOpen.value = false;
}

function afterAuth() {
  showAuthModal.value = false;
  getUser();
  getUserAsync();
  refreshUsage();
}
</script>

<template>
  <header class="fixed top-0 left-0 right-0 z-50">
    <div class="w-full px-4 mx-auto py-3 flex items-center justify-between">
      <!-- 左侧：Logo + 导航（合并容器，白色透明背景） -->
      <div
        class="flex items-center rounded-full p-1 transition-all duration-300"
        :class="[
          isScrolled ? 'bg-white/60 backdrop-blur-[40px]' : 'bg-transparent border-transparent',
        ]"
      >
        <router-link to="/" class="shrink-0 px-2">
          <div class="flex items-center gap-2">
            <img src="/images/seede-black.svg" alt="Seede Logo" class="h-7 w-28" />
          </div>
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex text-sm items-center gap-0 ml-1">
          <router-link
            to="/features"
            class="text-black/60 hover:text-black/90 transition-colors font-normal hover:bg-black/5 px-4 h-9 flex items-center rounded-full text-base"
          >
            <span class="flex items-center h-full leading-none">
              {{ $t('landing.features') }}
            </span>
          </router-link>

          <router-link
            to="/pricing"
            class="text-black/60 hover:text-black/90 transition-colors font-normal hover:bg-black/5 px-4 h-9 flex items-center rounded-full text-base"
          >
            <span class="flex items-center h-full leading-none">
              {{ $t('landing.pricing') }}
            </span>
          </router-link>
        </nav>
      </div>

      <!-- 右侧：积分升级 + 头像（合并容器，白色透明背景） -->
      <div class="flex items-center gap-1">
        <!-- Desktop Actions & Mobile User Menu -->
        <template v-if="!user.id">
          <div
            class="hidden md:flex items-center rounded-full px-2 py-1 gap-1 transition-all duration-300"
            :class="[
              isScrolled ? 'bg-white/80 backdrop-blur-[40px]' : 'bg-transparent border-transparent',
            ]"
          >
            <button
              @click="openAuthModal('sign-up')"
              class="text-black/60 text-sm hover:text-black/90 font-normal px-4 h-9 flex items-center rounded-full hover:bg-black/5 transition-all"
            >
              <span class="flex items-center h-full leading-none">
                {{ $t('landing.signUp') }}
              </span>
            </button>
            <button
              @click="openAuthModal('sign-up')"
              class="bg-black/80 text-white px-4 h-9 flex items-center rounded-full text-sm font-normal transition-all duration-200 ease-in-out hover:bg-black/90"
            >
              <span class="flex items-center h-full leading-none">
                {{ $t('landing.getStarted') }}
              </span>
            </button>
          </div>
        </template>
        <template v-else>
          <!-- 积分 + 升级 + 头像合并容器 -->
          <div
            class="hidden md:flex items-center rounded-full px-1 py-1 gap-1 transition-all duration-300"
            :class="[
              isScrolled ? 'bg-white/80 backdrop-blur-[40px]' : 'bg-transparent border-transparent',
            ]"
          >
            <!-- 积分按钮 - 点击跳转到定价页面 -->
            <router-link to="/pricing" class="shrink-0">
              <div
                class="relative h-9 flex items-center rounded-full transition-all duration-300 hover:bg-black/5 group cursor-pointer px-1"
              >
                <!-- 积分剩余信息 -->
                <div class="flex items-center gap-0.5 px-2 h-full">
                  <span
                    class="text-black/60 text-sm font-normal hidden lg:inline inline-flex items-center leading-none"
                  >
                    {{ $t('app.creditsRemaining') }}
                  </span>
                  <span class="inline-flex items-center opacity-60">
                    <CreditIcon />
                  </span>
                  <!-- 数字拨轮动画容器 -->
                  <span class="relative h-5 overflow-hidden inline-flex items-center">
                    <!-- 旧数字（动画时向上滚出） -->
                    <span
                      v-if="isAnimating"
                      class="absolute inset-0 text-black/60 text-sm font-normal inline-flex items-center leading-none animate-scroll-out"
                    >
                      {{ prevValue }}
                    </span>
                    <!-- 新数字（动画时从下方滚入） -->
                    <span
                      :class="[
                        'text-black/60 text-sm font-normal inline-flex items-center leading-none',
                        isAnimating ? 'animate-scroll-in' : '',
                      ]"
                    >
                      {{ displayValue }}
                    </span>
                  </span>
                </div>

                <!-- 升级按钮 -->
                <div
                  class="rounded-full px-3 h-8 flex items-center justify-center transition-all duration-200 hover:shadow-lg my-auto"
                  style="
                    background: linear-gradient(
                      to bottom,
                      rgba(255, 255, 255, 0.1),
                      rgba(255, 255, 255, 0.6)
                    );
                    box-shadow:
                      0px 1px 3px rgba(0, 0, 0, 0.05),
                      inset 0px 1px 1px rgba(255, 255, 255, 0.7);
                  "
                >
                  <span
                    class="text-black/90 text-sm font-normal inline-flex items-center h-full leading-none"
                  >
                    {{ $t('app.upgrade') }}
                  </span>
                </div>
              </div>
            </router-link>

            <!-- 用户头像和下拉菜单组件 -->
            <UserMenu />
          </div>
        </template>

        <!-- Mobile Menu Button -->
        <button
          class="md:hidden p-2 rounded-lg hover:bg-black/10 transition-colors"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          aria-label="Toggle menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-black/60"
          >
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="18" x2="20" y2="18" />
          </svg>
        </button>
      </div>

      <!-- Mobile Navigation -->
      <div
        v-if="isMobileMenuOpen"
        class="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden z-40"
        @click="isMobileMenuOpen = false"
      >
        <div class="absolute top-0 left-0 right-0 w-full bg-white shadow-lg" @click.stop>
          <div class="px-4 py-3 mx-auto">
            <!-- Mobile Menu Header with Logo -->
            <div class="flex items-center justify-between mb-6">
              <router-link to="/" @click="isMobileMenuOpen = false">
                <div class="flex items-center gap-2">
                  <img src="/images/seede-black.svg?v=2" alt="Seede Logo" class="h-8 w-32" />
                </div>
              </router-link>
              <button
                class="p-2 rounded-lg hover:bg-black/10 transition-colors"
                @click="isMobileMenuOpen = false"
                aria-label="Close menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-black/60"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <nav class="flex flex-col gap-2 mb-6">
              <router-link
                to="/features"
                class="text-black/60 hover:text-black/90 transition-colors font-normal p-3 rounded-lg hover:bg-black/5"
                @click="isMobileMenuOpen = false"
              >
                {{ $t('landing.features') }}
              </router-link>

              <router-link
                to="/pricing"
                class="text-black/60 hover:text-black/90 transition-colors font-normal p-3 rounded-lg hover:bg-black/5"
                @click="isMobileMenuOpen = false"
              >
                {{ $t('landing.pricing') }}
              </router-link>
            </nav>

            <div class="flex flex-col gap-3">
              <template v-if="!user.id">
                <button
                  @click="openAuthModal('sign-up')"
                  class="w-full text-black/70 border border-black/20 font-normal px-4 py-3 rounded-lg hover:bg-black/5 transition-all text-center"
                >
                  {{ $t('landing.signUp') }}
                </button>
                <button
                  @click="openAuthModal('sign-up')"
                  class="w-full bg-black/80 text-white px-4 py-3 rounded-lg font-normal transition-all duration-200 hover:bg-black/90"
                >
                  {{ $t('landing.getStarted') }}
                </button>
              </template>

              <template v-else>
                <div
                  class="bg-white/80 backdrop-blur-sm p-4 rounded-lg border border-black/10 mb-2"
                >
                  <div class="flex items-center gap-3 mb-4">
                    <Avatar
                      class="h-10 w-10 rounded-full bg-[#eefbf4] text-[#008667] font-normal flex items-center justify-center"
                    >
                      <AvatarImage :src="user.avatar || ''" :alt="user.name" />
                      <AvatarFallback class="rounded-lg">
                        {{ user.name?.slice(0, 1) || 'A' }}
                      </AvatarFallback>
                    </Avatar>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-normal text-black/80 truncate">
                        {{ user.name || user.email }}
                      </p>
                      <p class="text-xs text-black/50 truncate">
                        {{ user.email }}
                      </p>
                    </div>
                  </div>

                  <div class="space-y-1">
                    <router-link
                      to="/workspace"
                      class="flex items-center gap-2 w-full text-black/60 hover:text-black/90 hover:bg-black/5 rounded-lg p-2 transition-colors duration-200 text-sm"
                      @click="isMobileMenuOpen = false"
                    >
                      <svg
                        class="w-4 h-4 text-black/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      {{ $t('app.Workspace') }}
                    </router-link>

                    <router-link
                      to="/workspace"
                      class="flex items-center gap-2 w-full text-black/60 hover:text-black/90 hover:bg-black/5 rounded-lg p-2 transition-colors duration-200 text-sm"
                      @click="isMobileMenuOpen = false"
                    >
                      <svg
                        class="w-4 h-4 text-black/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                      {{ $t('app.generationHistory') }}
                    </router-link>

                    <router-link
                      to="/invite"
                      class="flex items-center gap-2 w-full text-black/60 hover:text-black/90 hover:bg-black/5 rounded-lg p-2 transition-colors duration-200 text-sm"
                      @click="isMobileMenuOpen = false"
                    >
                      <svg
                        class="w-4 h-4 text-black/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      {{ $t('app.invite') }}
                    </router-link>

                    <div class="h-px bg-black/10 my-2"></div>

                    <button
                      @click="
                        logout();
                        isMobileMenuOpen = false;
                      "
                      class="flex items-center gap-2 w-full text-red-600 hover:text-red-700 hover:bg-red-50/80 rounded-lg p-2 transition-colors duration-200 text-sm"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      {{ $t('app.logout') }}
                    </button>
                  </div>
                </div>

                <router-link to="/invite" class="w-full" @click="isMobileMenuOpen = false">
                  <button
                    class="w-full flex items-center justify-center gap-2 bg-black/80 text-white px-6 py-3 rounded-lg font-normal transition-all duration-200 hover:bg-black/90"
                  >
                    <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                    <span v-html="$t('invite.subtitle')"></span>
                  </button>
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Auth Modal -->
    <AuthModal v-model:show="showAuthModal" :login-type="authLoginType" @after-auth="afterAuth" />
  </header>
</template>

<style scoped>
/* 数字拨轮动画 - 旧数字向上滚出 */
@keyframes scroll-out {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 数字拨轮动画 - 新数字从下方滚入 */
@keyframes scroll-in {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-scroll-out {
  animation: scroll-out 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.animate-scroll-in {
  animation: scroll-in 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
</style>
